import {
  Building,
  Channel,
  Contact,
  DatabaseFile,
  Property,
  Unit,
} from '../../src/app/shared/models/entity';
import { BuildingBuilder } from './building.fixture';
import { ChannelBuilder } from './channel.fixture';
import { ContactBuilder } from './contact.fixture';
import { FileBuilder, createImageFile } from './database-file.fixture';
import { PropertyBuilder } from './property.fixture';
import { UnitBuilder } from './unit.fixture';

// -----------------------------------------------------------------------------------------------------
// @ TYPES
// -----------------------------------------------------------------------------------------------------

/**
 * A flat, coherent portfolio: every entity carries the right ids and denormalized names, but no
 * entity holds a relation instance. Flat is deliberate — the graph is cyclic by nature
 * (unit ⇄ contact, building ⇄ property) and a cyclic object graph cannot be JSON-serialised
 * by the mock backend. Use the `hydrate*` helpers below to attach relations one level deep
 * for the few callers that actually need them.
 */
export interface Dataset {
  properties: Property[];
  buildings: Building[];
  units: Unit[];
  contacts: Contact[];
  files: DatabaseFile[];
  channels: Channel[];
}

export interface DatasetOptions {
  /** How many properties to seed. */
  propertyCount?: number;
  /** How many buildings per property. */
  buildingsPerProperty?: number;
  /** How many units per building. */
  unitsPerBuilding?: number;
  /** How many contacts to seed. Owners are drawn from this pool, round-robin over the units. */
  contactCount?: number;
  /** How many documents per property. */
  filesPerProperty?: number;
  /** How many channels per property. */
  channelsPerProperty?: number;
}

export const DEFAULT_DATASET_OPTIONS: Required<DatasetOptions> = {
  propertyCount: 10,
  buildingsPerProperty: 2,
  unitsPerBuilding: 6,
  contactCount: 12,
  filesPerProperty: 4,
  channelsPerProperty: 3,
};

// -----------------------------------------------------------------------------------------------------
// @ FACTORY
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a coherent, deterministic portfolio.
 *
 * Everything is wired consistently: unit ↔ building ↔ property ids and denormalized names match,
 * `unitCount` / `totalArea` / `totalShares` are computed from the actual units, and every unit has
 * an owner drawn from the contact pool.
 *
 * @param options - How large the portfolio should be.
 * @returns {Dataset} The seeded, flat portfolio.
 */
export function createDataset(options: DatasetOptions = {}): Dataset {
  const config: Required<DatasetOptions> = { ...DEFAULT_DATASET_OPTIONS, ...options };

  const contacts: Contact[] = seedContacts(config.contactCount);

  const properties: Property[] = [];
  const buildings: Building[] = [];
  const units: Unit[] = [];
  const files: DatabaseFile[] = [];
  const channels: Channel[] = [];

  let buildingIndex: number = 0;
  let unitIndex: number = 0;
  let fileIndex: number = 0;
  let channelIndex: number = 0;
  let ownerIndex: number = 0;

  for (let propertyIndex = 0; propertyIndex < config.propertyCount; propertyIndex++) {
    const property: Property = new PropertyBuilder(propertyIndex).build();

    const propertyBuildings: Building[] = [];

    for (let index = 0; index < config.buildingsPerProperty; index++) {
      const building: Building = new BuildingBuilder(buildingIndex++).inProperty(property).build();

      const buildingUnits: Unit[] = [];

      for (let index = 0; index < config.unitsPerBuilding; index++) {
        const owner: Contact = contacts[ownerIndex++ % contacts.length];

        buildingUnits.push(
          new UnitBuilder(unitIndex++).inBuilding(building).ownedBy(owner).build(),
        );
      }

      units.push(...buildingUnits);

      propertyBuildings.push(
        new BuildingBuilder(0).with(building).withUnits(buildingUnits).build(),
      );
    }

    buildings.push(...propertyBuildings);

    // File has no back-reference to Property — it is referenced only from `documents` / `thumbnail`,
    // so the files are built standalone and attached from the property side.
    const thumbnail: DatabaseFile = createImageFile({}, propertyIndex);

    const propertyFiles: DatabaseFile[] = [
      thumbnail,
      ...Array.from({ length: config.filesPerProperty }, () =>
        new FileBuilder(fileIndex++).build(),
      ),
    ];

    const propertyChannels: Channel[] = Array.from({ length: config.channelsPerProperty }, () =>
      new ChannelBuilder(channelIndex++).inProperty(property).build(),
    );

    files.push(...propertyFiles);
    channels.push(...propertyChannels);

    properties.push(
      new PropertyBuilder(propertyIndex)
        .with(property)
        .withBuildings(propertyBuildings)
        .withDocuments(propertyFiles, thumbnail)
        .withChannels(propertyChannels)
        .build(),
    );
  }

  return {
    properties: properties.map((property: Property) => flatten(property, ['buildings'])),
    buildings: buildings.map((building: Building) => flatten(building, ['units'])),
    units,
    contacts,
    files,
    channels,
  };
}

// -----------------------------------------------------------------------------------------------------
// @ HYDRATION
// -----------------------------------------------------------------------------------------------------

/**
 * Attaches the buildings (and their units) of a property, one level deep.
 *
 * @param property - The flat property.
 * @param dataset - The dataset to resolve the relations from.
 * @returns {Property} A new Property instance with its buildings attached.
 */
export function hydrateProperty(property: Property, dataset: Dataset): Property {
  const buildings: Building[] = dataset.buildings
    .filter((building: Building) => building.propertyId === property.id)
    .map((building: Building) => hydrateBuilding(building, dataset));

  return new Property({
    ...property,
    buildings,
    contacts: ownersOf(
      buildings.flatMap((building: Building) => building.units),
      dataset,
    ),
  });
}

/**
 * Attaches the units of a building, one level deep.
 *
 * @param building - The flat building.
 * @param dataset - The dataset to resolve the relations from.
 * @returns {Building} A new Building instance with its units attached.
 */
export function hydrateBuilding(building: Building, dataset: Dataset): Building {
  const units: Unit[] = dataset.units.filter((unit: Unit) => unit.buildingId === building.id);

  return new Building({ ...building, units, unitCount: units.length });
}

/**
 * Attaches the owning contacts of a unit, one level deep.
 *
 * @param unit - The flat unit.
 * @param dataset - The dataset to resolve the relations from.
 * @returns {Unit} A new Unit instance with its contacts attached.
 */
export function hydrateUnit(unit: Unit, dataset: Dataset): Unit {
  const contacts: Contact[] = dataset.contacts.filter(
    (contact: Contact) => contact.name === unit.currentOwnerName,
  );

  return new Unit({ ...unit, contacts });
}

// -----------------------------------------------------------------------------------------------------
// @ PRIVATE
// -----------------------------------------------------------------------------------------------------

/**
 * Seeds the contact pool: mostly owners, with a couple of advisory-board members and a service provider.
 *
 * @param count - How many contacts to seed.
 * @returns {Contact[]} The seeded contacts.
 */
function seedContacts(count: number): Contact[] {
  return Array.from({ length: count }, (_, index: number) => {
    const builder: ContactBuilder = new ContactBuilder(index);

    if (index % 5 === 0 && index > 0) {
      builder.asAdvisoryBoard();
    }

    if (index === count - 1) {
      builder.asCompany();
    }

    return builder.build();
  });
}

/**
 * Resolves the distinct owners of a set of units.
 *
 * @param units - The units to resolve the owners of.
 * @param dataset - The dataset to resolve the contacts from.
 * @returns {Contact[]} The distinct owning contacts.
 */
function ownersOf(units: Unit[], dataset: Dataset): Contact[] {
  const names = new Set(units.map((unit: Unit) => unit.currentOwnerName));

  return dataset.contacts.filter((contact: Contact) => names.has(contact.name));
}

/**
 * Returns a copy of an entity with the given relation keys emptied, so the stored graph stays acyclic.
 *
 * @param entity - The entity to flatten.
 * @param relations - The relation keys to empty.
 * @returns {TModel} A new instance of the same class, without the given relations.
 */
function flatten<TModel extends object>(entity: TModel, relations: (keyof TModel)[]): TModel {
  const copy: TModel = Object.create(Object.getPrototypeOf(entity));

  Object.assign(copy, entity);

  for (const relation of relations) {
    (copy[relation] as unknown) = [];
  }

  return copy;
}
